import React from 'react';
import styled from 'styled-components';
import loadable from '@loadable/component';
import RightToolbarMobileComponent from '../../../UI/Editor/Toolbar/RightToolbarMobile';
import Backdrop from '../../../UI/Editor/Backdrop/Backdrop';
import {
  BlogsIcon,
  HelpIcon,
  HomeIcon,
  ManageAccountIcon,
  MyPlanIcon,
  PricingIcon,
  ReviewServicesIcon,
  TutorialIcon,
  LearnIcon,
} from '../../../editJournal/toolset/assets/icons';
import ItemComponent from '../../../../../../../../common/components/UI/Toolbar/ToolbarItems';
import redirectAppURL from '../../../../../../../../common/redirectApp';
import Logout from './Logout';
import breakpointsMinWidth from '../../../../../../styling/breakpointsMinWidth';
import { getUniversityServicesRestrictions } from '../../../../../../utility/universityRestrictions';
import { UNIVERSITYPLAN } from '../../../../../../utility/enums/planType';
import Accordion from '../../../../../../../../common/components/Accordion';
import UserProfile from './UserProfile';
import ProfileItemsList from './ProfileItemsList';

/* Styled Components */
export const ItemsWrapper = styled.div`
  border-top: ${(props) => !props.noBorder && '1px solid lightgray'};
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0rem;
`;

const ItemWrapper = styled.ul`
  padding-left: 60px;
`;

const Item = styled.li`
  text-align: left;
  list-style: none;
  font-size: 12px;
  margin: 0.6rem 0rem;
  margin-top: 0rem;
  color: #473880;
  @media ${breakpointsMinWidth.desktopMd} {
    font-size: 14px;
  }
`;

const LoginWrapper = styled.div`
  padding: ${(props) => props.appType !== 'GATSBY' && '0 24px'};
`;

const SignIn = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const HigherMobileToolbar = (props) => {
  const {
    rightList,
    userInfo,
    user,
    pathname,
    inviteId,
    appType,
    LinkComponent,
    showSideDrawer,
    sideDrawerClosedHandler,
    profileImageBlobURL,
    activePlan,
    subscriptionStatus,
    universityPlan,
    selectedPlan,
    userInfoFetched,
  } = props;

  const renderNavElements = (rightListItems) => {
    if (!LinkComponent) return null;

    const linkStyles = {
      color: 'none',
      textDecoration: 'none',
    };

    return rightListItems.map((navElement) => {
      if (
        navElement.text === 'My Drive' ||
        navElement.text === 'About Us' ||
        navElement.text === 'Refer' ||
        navElement.text === 'Pricing'
      ) {
        return null;
      }

      const childrenElements =
        navElement.childrenList &&
        navElement.childrenList.map((children) => {
          if (children.text === 'Coming Soon...') {
            return <Item key={children.text}>{children.text}</Item>;
          }

          return (
            <LinkComponent
              smooth
              to={children.url}
              style={linkStyles}
              onClick={sideDrawerClosedHandler}
              key={children.url}
              data-testid={children.text}
            >
              <Item key={children.text}>{children.text}</Item>
            </LinkComponent>
          );
        });

      return (
        <>
          {childrenElements && (
            <Accordion
              title={navElement.text}
              logoTitle={navElement.text}
              key={navElement.className}
              showSideDrawer={showSideDrawer}
              appType={appType}
            >
              <ItemWrapper>{childrenElements}</ItemWrapper>
            </Accordion>
          )}
        </>
      );
    });
  };

  const handlePlan = (planName) => {
    const textArr = planName?.split('_');
    const [planGrp, planType, planDuration] = textArr;

    return {
      planGrp,
      planType,
      planDuration,
    };
  };

  const renderMobile = () => {
    let ProfileIcon;

    if (appType === 'CRA') {
      ProfileIcon = loadable(() => import('./profileIcon'));
    }
    if (!LinkComponent) return null;

    const handleClick = () => {
      sideDrawerClosedHandler();
      document.dispatchEvent(new Event('sideBarClosed'));
    };

    const getUserPlan = () => {
      switch (selectedPlan) {
        case 'universityPlan':
          if (!universityPlan) return '';
          return universityPlan.plan;
        case 'usersPlan':
          if (activePlan && subscriptionStatus)
            return handlePlan(activePlan.name).planType;
          return 'Free';
        default:
          return 'Free';
      }
    };
    const userObj = {
      name: userInfo?.profile?.name,
      email: userInfo?.profile?.email,
      planType: getUserPlan(),
      profileImage: profileImageBlobURL,
    };

    let childList = [
      {
        type: 'intURL',
        text: 'Home',
        icon: <HomeIcon height="20" width="20" />,
        seperator: false,
        url: '/drive/apps',
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'intURL',
        text: 'Manage my account',
        icon: <ManageAccountIcon height="20" width="20" />,
        seperator: false,
        url: '/account',
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'intURL',
        url: '/billings',
        icon: <MyPlanIcon height="20" width="20" />,
        seperator: true,
        text: 'My Plan',
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'intURL',
        text: 'Feedback',
        icon: <HelpIcon height="20" width="20" />,
        seperator: true,
        url: '/support',
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'extURL',
        url: `https://youtu.be/poi9l_2LMsk`,
        text: 'Tutorial',
        newTab: true,
        icon: <TutorialIcon height="20" width="20" />,
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'extURL',
        text: 'Blogs',
        icon: <BlogsIcon width="20" height="20" />,
        seperator: true,
        url: `https://wisedoc.net/blogs?appType=CRA&path=/blogs`, // Here we are hard coding this URL temporarily because config is associated with multiple places.
        newTab: true,
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'intURL',
        url: `/pricing`,
        icon: <PricingIcon height="20" width="20" />,
        text: 'Pricing',
        onClick: sideDrawerClosedHandler,
      },
      {
        type: 'intURL',
        url: '/learn',
        icon: <LearnIcon height="20" width="20" />,
        text: 'Learn',
        onClick: sideDrawerClosedHandler,
      },
    ];

    const isOutsideLogin = userInfo && !userInfo.token;
    const hasReviewServices =
      userInfoFetched &&
      selectedPlan === UNIVERSITYPLAN &&
      !getUniversityServicesRestrictions(universityPlan)?.iris;

    if (hasReviewServices) {
      const reviewServices = {
        type: 'intURL',
        text: 'Review Services',
        icon: <ReviewServicesIcon height="20" width="20" />,
        url: '/services',
        onClick: sideDrawerClosedHandler,
      };

      childList = [...childList, reviewServices];
    }

    if (!isOutsideLogin) {
      const logout = {
        type: 'children',
        text: 'Logout',
        children: <Logout {...props} />,
      };
      childList = [...childList, logout];
    }

    return (
      <>
        <Backdrop show={showSideDrawer} clicked={handleClick} />
        <RightToolbarMobileComponent
          pathname={pathname}
          user={user}
          open={showSideDrawer}
          isOutsideLogin={isOutsideLogin}
        >
          {isOutsideLogin ? (
            <LoginWrapper appType={appType}>
              {ProfileIcon ? (
                <ProfileIcon
                  sideDrawerClosedHandler={sideDrawerClosedHandler}
                  appType={appType}
                />
              ) : (
                <ItemComponent className="signIn" pathname={pathname}>
                  <SignIn
                    href={redirectAppURL({
                      appType,
                      path: '/drive/myDrive',
                    })}
                    target="_blank"
                  >
                    <Accordion
                      title="Login"
                      logoTitle="Login"
                      key="Login"
                      fill="none"
                      showSideDrawer={showSideDrawer}
                    />
                  </SignIn>
                </ItemComponent>
              )}
            </LoginWrapper>
          ) : (
            <UserProfile user={userObj} />
          )}

          {isOutsideLogin && (
            <ItemsWrapper>{renderNavElements(rightList)}</ItemsWrapper>
          )}
          {isOutsideLogin ? (
            <ItemsWrapper>
              <LinkComponent
                to="/blogs"
                style={{ color: 'none', textDecoration: 'none' }}
                onClick={sideDrawerClosedHandler}
              >
                <Accordion
                  title="Blogs"
                  logoTitle="Blogs"
                  key="blogs"
                  fill="none"
                  showSideDrawer={showSideDrawer}
                />
              </LinkComponent>

              <LinkComponent
                to="/pricing"
                style={{ color: 'none', textDecoration: 'none' }}
                onClick={sideDrawerClosedHandler}
              >
                <Accordion
                  title="Pricing"
                  logoTitle="Pricing"
                  key="pricing"
                  fill="none"
                  showSideDrawer={showSideDrawer}
                />
              </LinkComponent>

              <LinkComponent
                to="/about-us"
                style={{ color: 'none', textDecoration: 'none' }}
                onClick={sideDrawerClosedHandler}
              >
                <Accordion
                  title="About Us"
                  logoTitle="AboutUs"
                  key="aboutUs"
                  fill="none"
                  showSideDrawer={showSideDrawer}
                />
              </LinkComponent>
            </ItemsWrapper>
          ) : (
            <ProfileItemsList childrenList={childList} inviteId={inviteId} />
          )}
        </RightToolbarMobileComponent>
      </>
    );
  };

  return <>{renderMobile()}</>;
};
export default HigherMobileToolbar;
